import React from 'react';
import { Button } from 'src/components';
import styles from './VersionsTab.module.scss';

type MakeLiveDialogProps = {
  assetName: string;
  liveVersion: number;
  version: number;
  /** a previous version going live again (restore) rather than a pending one (release) */
  isRestore: boolean;
  /** the oldest previous version is deleted when this one goes live */
  dropsOldest: number | null;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Explicit confirmation before a version becomes the live content. Making a
 * version live changes the file, renditions and CDN links every viewer,
 * share and embed sees, so it is never a single click.
 */
export const MakeLiveDialog: React.FC<MakeLiveDialogProps> = ({
  assetName,
  liveVersion,
  version,
  isRestore,
  dropsOldest,
  busy,
  onConfirm,
  onCancel,
}) => (
  <div className={styles.overlay} data-test="make-live-dialog">
    <div className={styles.backdrop} onClick={busy ? undefined : onCancel} />
    <div className={styles.dialog} role="alertdialog" aria-labelledby="make-live-title">
      <h3 id="make-live-title">
        {isRestore ? `Restore version ${version}?` : `Make version ${version} live?`}
      </h3>
      <p>
        <strong>{assetName}</strong> will switch from version {liveVersion} to version {version}
        {isRestore ? ' (restored as a new version number)' : ''}.
      </p>
      <ul className={styles.dialogList}>
        <li>Everyone sees the new file: the dashboard, shares, embeds and API.</li>
        <li>Formats are regenerated from it, and cached CDN links are refreshed; this can take a few minutes.</li>
        <li>Version {liveVersion} is kept as a previous version you can restore.</li>
        {dropsOldest !== null && (
          <li className={styles.dialogWarning}>
            Your account keeps a limited number of previous versions, so version {dropsOldest} will be deleted.
          </li>
        )}
      </ul>
      <div className={styles.dialogControls}>
        <Button mode="flat" size="small" onClick={onCancel} disabled={busy} datatest="make-live-cancel">
          Cancel
        </Button>
        <Button mode="primary" size="small" onClick={onConfirm} disabled={busy} datatest="make-live-confirm">
          {busy ? 'Working…' : isRestore ? `Restore version ${version}` : `Make version ${version} live`}
        </Button>
      </div>
    </div>
  </div>
);

export default MakeLiveDialog;
